import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  TextField,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '@redux/hooks';
import { fetchPermissions, setImpersonation } from '@redux/accounts';
import { popoverShadow } from 'theme/shadows';

type Account = { fssId: string; zohoCrmId: string; name: string };

const filterAccounts = createFilterOptions<Account>();

const isTypingKey = (event: React.KeyboardEvent) =>
  event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;

export const AccountSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const { list, selected } = useSelector((state: RootState) => state.accounts);
  const [open, setOpen] = useState(false);
  const [searchable, setSearchable] = useState(false);
  const [query, setQuery] = useState('');
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  const closeMenu = useCallback(() => {
    // iOS keeps the software keyboard up for as long as the field holds focus,
    // whatever readOnly then says, so closing has to hand focus back.
    if (window.matchMedia('(hover: none)').matches) {
      fieldRef.current?.querySelector('input')?.blur();
    }
    setOpen(false);
    setSearchable(false);
  }, []);

  // The nav drawer is keepMounted, so closing it only hides it — without this the
  // menu survives in its own portal as a list floating over the page.
  useEffect(() => {
    if (!open) return undefined;

    const closeIfOutside = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (fieldRef.current?.contains(target)) return;
      if (target.closest('.MuiAutocomplete-popper')) return;

      closeMenu();
    };

    document.addEventListener('pointerdown', closeIfOutside);
    return () => document.removeEventListener('pointerdown', closeIfOutside);
  }, [open, closeMenu]);

  const handleChange = async (account: Account | null) => {
    if (!account) return;

    const result = await dispatch(
      setImpersonation({ id: account.fssId, name: account.name }),
    );

    if (setImpersonation.fulfilled.match(result)) {
      dispatch(fetchPermissions());
    }
  };

  const options = useMemo(
    () => list.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [list],
  );

  const value =
    options.find((account) => account.fssId === selected) ?? options[0];

  const openMenu = () => {
    setQuery(value?.name ?? '');
    setSearchable(true);
    setOpen(true);
  };

  if (!list.length) return null;

  return (
    <Box ref={fieldRef} sx={{ position: 'relative', width: '100%' }}>
      <Autocomplete
        open={open}
        onOpen={openMenu}
        onClose={closeMenu}
        options={options}
        value={value}
        inputValue={searchable ? query : value?.name ?? ''}
        onInputChange={(_, next, reason) => {
          if (reason === 'input') setQuery(next);
        }}
        onChange={(_, account) => handleChange(account)}
        getOptionLabel={(account) => account.name}
        isOptionEqualToValue={(option, selectedOption) =>
          option.fssId === selectedOption.fssId
        }
        filterOptions={(accounts, state) =>
          !searchable || state.inputValue === value?.name
            ? accounts
            : filterAccounts(accounts, state)
        }
        disableClearable
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Account"
            inputProps={{
              ...params.inputProps,
              readOnly: !searchable,
              onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
                if (searchable && event.currentTarget.value === value?.name) {
                  setQuery('');
                }
                params.inputProps.onFocus?.(event);
              },
              onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  searchable &&
                  isTypingKey(event) &&
                  event.currentTarget.value === value?.name
                ) {
                  event.currentTarget.value = '';
                  setQuery('');
                }
                params.inputProps.onKeyDown?.(event);
              },
            }}
          />
        )}
        slotProps={{
          popper: { placement: 'top-start' },
          paper: {
            sx: {
              boxShadow: popoverShadow,
              '& .MuiAutocomplete-listbox': {
                maxHeight: '60dvh',
              },
            },
          },
        }}
        sx={{
          backgroundColor: (theme) => theme.palette.background.selectBackground,
          borderRadius: 1,
          px: 1,
          py: 0.5,
          width: '100%',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      />
      {!searchable && (
        // Keeps the opening tap off the input, so the next tap is that input's first
        // focus — the only thing iOS accepts as a reason to raise the keyboard.
        <Box
          component="span"
          aria-hidden
          onClick={openMenu}
          sx={{
            position: 'absolute',
            inset: 0,
            cursor: 'pointer',
            borderRadius: 1,
          }}
        />
      )}
    </Box>
  );
};
